<template>
  <div v-show="active" class="tab-info p-4">
    <!-- Título y badge de estado -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2 class="platform-title text-white mb-0">{{ platform.name }}</h2>
      <span
        v-if="statusText"
        class="badge"
        :class="statusClass"
        style="font-size: 0.9rem; padding: 0.5rem 1rem;"
      >
        {{ statusText }}
      </span>
    </div>

    <!-- Descripción -->
    <div class="col-12 mb-4">
      <p
        class="platform-description text-white text-justify"
        v-html="formatText(platform.description)"
      ></p>
    </div>

    <!-- Fecha inicio y duración -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-3" v-if="platform.date_start">
        <div class="info-input">
          <span class="info-label">Fecha de inicio: {{ platform.date_start ? moment(platform.date_start, 'YYYY-MM-DD').format('DD/MM/YYYY') : 'Sin fecha' }}</span>
        </div>
      </div>
      <div class="col-6 col-md-3" v-if="platform.duration">
        <div class="info-input">
          <span class="info-label">Duración: {{ platform.duration }}</span>
        </div>
      </div>
    </div>

    <!-- Detalles esenciales -->
    <h6 class="text-white mb-3">Detalles esenciales</h6>
    <div class="row g-3">
      <!-- Columna izquierda -->
      <div class="col-12 col-md-8">
        <div class="row g-3">
          <div class="col-6" v-if="hasLicenses">
            <div class="info-input">
              <span class="info-label">Licencia requerida: {{ licensesText }}</span>
            </div>
          </div>
          <div class="col-6" v-if="platform.type_platform">
            <div class="info-input">
              <span class="info-label">Modalidad: {{ platform.type_platform.name }}</span>
            </div>
          </div>
          <div class="col-6" v-if="platform.type_platform_category">
            <div class="info-input">
              <span class="info-label">Tipo de curso: {{ platform.type_platform_category.name }}</span>
            </div>
          </div>
          <div class="col-6" v-if="platform.type_platform_speciality">
            <div class="info-input">
              <span class="info-label">Disciplina: {{ platform.type_platform_speciality.name }}</span>
            </div>
          </div>
          <div class="col-12" v-if="platform.instructors">
            <div class="info-input">
              <span class="info-label">Instructores: {{ platform.instructors }}</span>
            </div>
          </div>
          <div class="col-12" v-if="platform.requires">
            <h6 class="text-white mb-2">Requisitos</h6>
            <div class="info-input info-input-multiline">
              <p class="mb-0 mt-1" v-html="formatText(platform.requires)"></p>
            </div>
          </div>
          <div class="col-12" v-if="showAccessButton">
            <button
              class="btn btn-fvf-access position-relative overflow-hidden w-100 py-3"
              @click.prevent="$emit('access')"
            >
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span class="position-relative z-index-1 text-uppercase fw-bold letter-spacing-1">
                Acceder al enlace de la plataforma
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Columna derecha: video -->
      <div class="col-12 col-md-4" v-if="platform.url_video">
        <div class="video-container">
          <iframe
            v-if="videoEmbedUrl"
            :src="videoEmbedUrl"
            name="video-iframe"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            class="video-player"
          ></iframe>
          <div v-else class="video-placeholder">
            <iconify-icon icon="lucide:play-circle" class="video-icon" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import moment from 'moment';

const props = defineProps({
  active: {
    type: Boolean,
    default: false,
  },
  platform: {
    type: Object,
    default: () => ({}),
  },
  statusText: {
    type: String,
    default: '',
  },
  statusClass: {
    type: String,
    default: '',
  },
  showAccessButton: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['access']);

const hasLicenses = computed(() => {
  return Array.isArray(props.platform.licenses) && props.platform.licenses.length > 0;
});

const licensesText = computed(() => {
  if (!hasLicenses.value) return '';
  return props.platform.licenses.map((l) => l.name || l).join(', ');
});

const videoEmbedUrl = computed(() => {
  const url = props.platform.url_video;
  if (!url) return null;
  const regExp = /(?:youtube\.com\/(?:[^\n\s]+\/\S+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
  const match = url.match(regExp);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
});

const formatText = (text) => {
  if (!text) return '';
  return text.replace(/(\r\n|\n|\r)/g, '<br>');
};

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return moment(d.toISOString()).format('DD-MM-YYYY');
};
</script>

<style scoped>
.tab-info {
  color: #fff;
}

.platform-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.platform-description {
  font-size: 0.95rem;
  line-height: 1.6;
  opacity: 0.9;
}

.info-input {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 12px 16px;
  color: #fff;
  font-size: 0.9rem;
  min-height: 48px;
  display: flex;
  align-items: center;
}

.info-input-multiline {
  flex-direction: column;
  align-items: flex-start;
  min-height: auto;
}

.info-label {
  color: #fff;
  font-size: 0.9rem;
}

.video-container {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 16/9;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.video-player {
  width: 100%;
  height: 100%;
}

.video-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.2);
}

.video-icon {
  font-size: 64px;
  color: rgba(255, 255, 255, 0.5);
}

.btn-fvf-access {
  background: linear-gradient(135deg, #500e22 0%, #8d1b3d 50%, #500e22 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  position: relative;
  transition: all 0.3s ease;
  overflow: hidden;
  text-transform: uppercase;
  font-weight: bold;
  letter-spacing: 1px;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 15px rgba(112, 19, 47, 0.4);
}

.btn-fvf-access::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background:
    radial-gradient(circle at 50% 50%, rgba(197, 160, 89, 0.2) 0%, transparent 20%),
    radial-gradient(circle at 10% 20%, rgba(197, 160, 89, 0.15) 0%, transparent 20%),
    radial-gradient(circle at 90% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 15%);
  background-size: 50% 50%, 60% 60%, 50% 50%;
  animation: lava-flow 10s ease-in-out infinite alternate;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.btn-fvf-access:hover::before {
  opacity: 1;
}

@keyframes lava-flow {
  0% { transform: rotate(0deg) translate(0, 0); }
  33% { transform: rotate(5deg) translate(10px, 10px); }
  66% { transform: rotate(-5deg) translate(-10px, 5px); }
  100% { transform: rotate(0deg) translate(0, 0); }
}

.btn-fvf-access:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(197, 160, 89, 0.4);
  color: #fff;
}

.btn-fvf-access span:nth-child(1),
.btn-fvf-access span:nth-child(2),
.btn-fvf-access span:nth-child(3),
.btn-fvf-access span:nth-child(4) {
  position: absolute;
  display: block;
}

.btn-fvf-access span:nth-child(1) {
  top: 0;
  left: -100%;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, transparent, #c5a059);
  animation: btn-anim1 2s linear infinite;
}

@keyframes btn-anim1 {
  0% { left: -100%; }
  50%, 100% { left: 100%; }
}

.btn-fvf-access span:nth-child(2) {
  top: -100%;
  right: 0;
  width: 3px;
  height: 100%;
  background: linear-gradient(180deg, transparent, #c5a059);
  animation: btn-anim2 2s linear infinite;
  animation-delay: 0.5s;
}

@keyframes btn-anim2 {
  0% { top: -100%; }
  50%, 100% { top: 100%; }
}

.btn-fvf-access span:nth-child(3) {
  bottom: 0;
  right: -100%;
  width: 100%;
  height: 3px;
  background: linear-gradient(270deg, transparent, #c5a059);
  animation: btn-anim3 2s linear infinite;
  animation-delay: 1s;
}

@keyframes btn-anim3 {
  0% { right: -100%; }
  50%, 100% { right: 100%; }
}

.btn-fvf-access span:nth-child(4) {
  bottom: -100%;
  left: 0;
  width: 3px;
  height: 100%;
  background: linear-gradient(360deg, transparent, #c5a059);
  animation: btn-anim4 2s linear infinite;
  animation-delay: 1.5s;
}

@keyframes btn-anim4 {
  0% { bottom: -100%; }
  50%, 100% { bottom: 100%; }
}

@media (max-width: 768px) {
  .platform-title {
    font-size: 1.5rem;
  }

  .video-container {
    margin-top: 1rem;
  }
}
</style>
